/**
 * /staff/usage/runs — tokens y USD por FASE por cotización/corrida, más el
 * hit-rate de la biblioteca de análisis con IA (reúso a $0 de LLM).
 *
 * Es la vista del punto 6 del reporte de gasto de tokens (2026-08-06): medir
 * por fase ANTES de recalibrar multiplicadores. Los datos salen de `ai_usage`
 * (metadata.quote_id + purpose) vía /api/v1/staff/usage/runs.
 */
import { formatCreditos } from '../lib/creditos.js';
import { useEffect, useState, Fragment } from 'react';
import { Navigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Spinner,
  MessageBar,
  MessageBarBody,
  Field,
} from '@fluentui/react-components';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { useAuthStore } from '../stores/authStore.js';
import {
  listStaffRunsUsage,
  getStaffApuLibraryStats,
  type RunUsageSummary,
  type ApuLibraryStats,
} from '../api/staffUsage.js';
import { ApiClientError } from '../api/client.js';
import { formatUsd, fmtDateTime, monthRangeFromInput, todayIsoEndOfDay } from '../lib/staffUsageFormat.js';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  content: { flex: 1, padding: '24px 32px', maxWidth: '1280px', width: '100%', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '16px', flexWrap: 'wrap' },
  title: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold },
  filters: { display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px' },
  filterField: { minWidth: '160px' },
  kpis: { display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '16px', padding: '12px 14px', background: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium, border: `1px solid ${tokens.colorNeutralStroke2}` },
  kpi: { display: 'flex', flexDirection: 'column', gap: '2px' },
  kpiLabel: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  kpiValue: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, fontVariantNumeric: 'tabular-nums' },
  tableWrap: { border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, overflow: 'hidden', background: tokens.colorNeutralBackground1 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 14px', background: tokens.colorNeutralBackground2, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' },
  thRight: { textAlign: 'right' },
  td: { padding: '10px 14px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, fontSize: tokens.fontSizeBase300 },
  tdRight: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  runRow: { cursor: 'pointer', ':hover': { background: tokens.colorNeutralBackground1Hover } },
  phaseRow: { background: tokens.colorNeutralBackground2 },
  phaseTd: { padding: '6px 14px 6px 32px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2 },
  emptyRow: { padding: '24px 14px', textAlign: 'center', color: tokens.colorNeutralForeground3 },
});

/** Nombres legibles de las fases del pipeline (fallback: la fase cruda). */
const PHASE_LABEL: Record<string, string> = {
  extract: 'Extracción (HP)',
  hierarchy: 'Jerarquía',
  match: 'Match / cotización',
  quote: 'Match / cotización',
  quote_embed: 'Embeddings de conceptos',
  apu_ia: 'Análisis con IA',
  market_price: 'Precio de mercado',
  chat: 'Chat',
  summary: 'Resumen de obra',
  embedding: 'Embeddings de documentos',
  vision: 'Visión (escaneados/planos)',
  web_search: 'Búsqueda web',
};

function daysAgoIso(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}

export function StaffRunsUsagePage() {
  const styles = useStyles();
  const tenant = useAuthStore((s) => s.tenant);
  const isStaff =
    !!tenant?.isEcosoftOwned && (tenant.role === 'admin' || tenant.role === 'owner');

  const [from, setFrom] = useState<string>(() => daysAgoIso(30));
  const [to, setTo] = useState<string>(() => todayIsoEndOfDay());
  const [runs, setRuns] = useState<RunUsageSummary[] | null>(null);
  const [library, setLibrary] = useState<ApuLibraryStats | null>(null);
  const [openRun, setOpenRun] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isStaff) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const range = { from: monthRangeFromInput(from, 'start'), to: monthRangeFromInput(to, 'end') };
    Promise.all([
      listStaffRunsUsage({ ...range, limit: 200 }),
      getStaffApuLibraryStats(range),
    ])
      .then(([r, lib]) => {
        if (cancelled) return;
        setRuns(r.runs);
        setLibrary(lib);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiClientError ? e.message : 'Error al cargar corridas');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isStaff, from, to]);

  if (!isStaff) return <Navigate to="/" replace />;

  const fmtTok = (n: number) => n.toLocaleString('es-MX');
  const totalUsd = (runs ?? []).reduce((a, r) => a + r.totalCostUsd, 0);

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs
        items={[
          { label: 'Inicio', to: '/projects' },
          { label: 'Staff' },
          { label: 'Consumo por tenant', to: '/staff/usage' },
          { label: 'Por corrida' },
        ]}
      />
      <main className={styles.content}>
        <div className={styles.header}>
          <Text as="h1" className={styles.title}>Costo por corrida (fase por fase)</Text>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            Vista restringida — Ecosoft staff
          </Text>
        </div>

        <div className={styles.filters}>
          <Field label="Desde" className={styles.filterField}>
            <Input type="date" value={from.slice(0, 10)} onChange={(_e, d) => setFrom(d.value)} />
          </Field>
          <Field label="Hasta" className={styles.filterField}>
            <Input type="date" value={to.slice(0, 10)} onChange={(_e, d) => setTo(d.value)} />
          </Field>
        </div>

        {library && (
          <div className={styles.kpis}>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>USD del rango (corridas listadas)</span>
              <span className={styles.kpiValue}>{formatUsd(totalUsd)}</span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel} title="APUs del rango servidos desde la biblioteca de análisis con IA: costaron $0 de LLM.">
                Reúso biblioteca APU (rango)
              </span>
              <span className={styles.kpiValue}>
                {library.hitRate != null
                  ? `${(library.hitRate * 100).toFixed(1)} % (${fmtTok(library.periodFromLibrary)} de ${fmtTok(library.periodApus)})`
                  : 'Sin APUs en el rango'}
              </span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>Biblioteca (recetas aprobadas / total)</span>
              <span className={styles.kpiValue}>{fmtTok(library.libraryApproved)} / {fmtTok(library.libraryRows)}</span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>Reúsos históricos acumulados</span>
              <span className={styles.kpiValue}>{fmtTok(library.libraryReuses)}</span>
            </div>
          </div>
        )}

        {error && (
          <MessageBar intent="error" style={{ marginBottom: 16 }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Corrida (quote)</th>
                <th className={styles.th}>Tenant</th>
                <th className={styles.th}>Última actividad</th>
                <th className={`${styles.th} ${styles.thRight}`}>Tokens in</th>
                <th className={`${styles.th} ${styles.thRight}`}>Tokens out</th>
                <th className={`${styles.th} ${styles.thRight}`} title="Consumo real del proveedor ÷ 10,000 (sin factor)">Créditos (real)</th>
                <th className={`${styles.th} ${styles.thRight}`}>USD</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td className={styles.emptyRow} colSpan={6}>
                    <Spinner size="tiny" label="Cargando…" />
                  </td>
                </tr>
              )}
              {!loading && runs && runs.length === 0 && (
                <tr>
                  <td className={styles.emptyRow} colSpan={6}>
                    Sin corridas con consumo IA en el rango.
                  </td>
                </tr>
              )}
              {!loading && runs?.map((run) => (
                <Fragment key={run.quoteId}>
                  <tr
                    className={styles.runRow}
                    onClick={() => setOpenRun(openRun === run.quoteId ? null : run.quoteId)}
                    title="Clic para ver el desglose por fase"
                  >
                    <td className={styles.td} style={{ fontFamily: 'monospace', fontSize: tokens.fontSizeBase200 }}>
                      {openRun === run.quoteId ? '▾ ' : '▸ '}{run.quoteId.slice(0, 8)}…
                    </td>
                    <td className={styles.td}>{run.tenantSlug}</td>
                    <td className={styles.td}>{fmtDateTime(run.lastAt) ?? '—'}</td>
                    <td className={`${styles.td} ${styles.tdRight}`}>{fmtTok(run.totalInputTokens)}</td>
                    <td className={`${styles.td} ${styles.tdRight}`}>{formatCreditos(run.totalInputTokens + run.totalOutputTokens, 'desglose')}</td>
                    <td className={`${styles.td} ${styles.tdRight}`}><strong>{formatUsd(run.totalCostUsd)}</strong></td>
                  </tr>
                  {openRun === run.quoteId && run.phases.map((p, i) => (
                    <tr key={`${run.quoteId}-${p.phase}-${p.provider}-${i}`} className={styles.phaseRow}>
                      <td className={styles.phaseTd} colSpan={2}>
                        {PHASE_LABEL[p.phase] ?? p.phase}
                        <span style={{ color: tokens.colorNeutralForeground4 }}> · {p.provider || 'sin proveedor'}</span>
                      </td>
                      <td className={styles.phaseTd}>{p.calls} llamada{p.calls === 1 ? '' : 's'}</td>
                      <td className={`${styles.phaseTd} ${styles.tdRight}`}>{fmtTok(p.inputTokens)}</td>
                      <td className={`${styles.phaseTd} ${styles.tdRight}`}>{fmtTok(p.outputTokens)}</td>
                      <td className={`${styles.phaseTd} ${styles.tdRight}`}>{formatCreditos(p.inputTokens + p.outputTokens, 'desglose')}</td>
                      <td className={`${styles.phaseTd} ${styles.tdRight}`}>{formatUsd(p.costUsd)}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
